import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native-gesture-handler';

import { useNavigation } from '@react-navigation/native';

import { RightChevron } from '@/assets/icons/right';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { findValidLineupIndex } from '@/feature/lobby/utils/lineupsUtils';
import { PickemLobbyTabNavigationProp } from '@/navigation/pickem/types';
import { common } from '@/styles/styles';

import { useLobbyData } from '../hooks/LobbyDataProvider';
import { useLatestLineups } from '../hooks/use-latest-lineups';
import { PrebuiltLineupCard } from './PrebuiltLineupCard';
import { FeaturedLineupSkeleton } from './Skeletons/FeaturedLineupSkeleton';

export const FeaturedLineupSection = memo(({ title }: { title: string }) => {
    const navigation = useNavigation<PickemLobbyTabNavigationProp>();
    const { t } = useTranslation('betslip_pickem');
    const [randomIndex, setRandomIndex] = useState<number | null>(null);

    const { sharedEntries, hasLobbyData } = useLobbyData();

    const filteredLineups = useLatestLineups(sharedEntries);

    useEffect(() => {
        const filteredLineupsLength = filteredLineups?.length;

        if (randomIndex === null && filteredLineupsLength > 0) {
            const validLineupIndex = findValidLineupIndex(filteredLineups);

            setRandomIndex(validLineupIndex);
        }
    }, [filteredLineups, randomIndex]);

    const randomPrebuiltLineup = randomIndex !== null ? filteredLineups?.[randomIndex] : null;

    const navigateToFeaturedLineups = () => {
        BetrAnalytics.trackEvent(AnalyticsEvent.VIEW_FEATURED_LINEUPS);
        navigation.navigate('FeaturedLineupsScreen', { title });
    };

    if (hasLobbyData && (sharedEntries?.length === 0 || randomIndex === -1)) {
        return null;
    }

    return (
        <Box paddingBottom={'s28'}>
            <Row marginHorizontal={'s16'} marginBottom={'s20'} justifyContent={'space-between'} alignItems={'center'}>
                <Text color={ds.colorToken.text.primary} variant={'headlineMedium'} lineHeight={28}>
                    {title}
                </Text>
                {sharedEntries && sharedEntries.length > 1 ? (
                    <TouchableOpacity onPress={navigateToFeaturedLineups} hitSlop={20} activeOpacity={0.8}>
                        <Row style={[common.justifyCenter, common.alignCenter]}>
                            <Text variant={'titleSmall'}>{t('view_all')}</Text>
                            <SizedBox value={10} />
                            <RightChevron />
                        </Row>
                    </TouchableOpacity>
                ) : null}
            </Row>
            {!hasLobbyData ? (
                <FeaturedLineupSkeleton />
            ) : randomPrebuiltLineup ? (
                <PrebuiltLineupCard prebuiltLineup={randomPrebuiltLineup} index={0} />
            ) : null}
        </Box>
    );
});
